/**
 * Resilient Axios Client for Kiosk Environments
 *
 * Features:
 * - Dynamic base URL from config
 * - Request timeout (10s default)
 * - Automatic retry with exponential backoff + jitter
 * - Circuit breaker: pauses requests after consecutive failures
 * - Error tagging: isOffline, isTransient flags
 * - Graceful error handling with safeGet/safePost
 */

import axios from 'axios';
import { getApiBaseUrl_dynamic } from './config.js';

// ─── Configuration ───────────────────────────────────────────────────────────
const DEFAULT_TIMEOUT_MS = 10000; // 10 second timeout
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY_MS = 1000; // 1 second
const MAX_RETRY_DELAY_MS = 8000; // 8 seconds

// Circuit breaker settings
const CIRCUIT_BREAKER_THRESHOLD = 3; // failures before opening circuit
const CIRCUIT_BREAKER_RESET_MS = 15000; // 15 seconds pause when circuit opens

// ─── Circuit Breaker State ───────────────────────────────────────────────────
let circuitBreakerFailures = 0;
let circuitBreakerOpenUntil = 0;

// Connectivity state callbacks (set by ConnectivityContext)
let onOfflineCallback = null;
let onOnlineCallback = null;

/**
 * Register callbacks for connectivity state changes
 */
export const setConnectivityCallbacks = (onOffline, onOnline) => {
  onOfflineCallback = onOffline;
  onOnlineCallback = onOnline;
};

/**
 * Check if circuit breaker is open (blocking requests)
 */
export const isCircuitOpen = () => {
  if (circuitBreakerOpenUntil === 0) return false;
  if (Date.now() >= circuitBreakerOpenUntil) {
    // Circuit has reset
    circuitBreakerOpenUntil = 0;
    circuitBreakerFailures = 0;
    console.log('🔌 Circuit breaker reset - allowing requests');
    return false;
  }
  return true;
};

/**
 * Get circuit breaker status for debugging
 */
export const getCircuitBreakerStatus = () => ({
  failures: circuitBreakerFailures,
  isOpen: isCircuitOpen(),
  opensAt: circuitBreakerOpenUntil,
  remainingMs: Math.max(0, circuitBreakerOpenUntil - Date.now())
});

/**
 * Reset circuit breaker (called when health check succeeds)
 */
export const resetCircuitBreaker = () => {
  if (circuitBreakerFailures > 0 || circuitBreakerOpenUntil > 0) {
    console.log('✅ Circuit breaker reset - connectivity restored');
  }
  circuitBreakerFailures = 0;
  circuitBreakerOpenUntil = 0;
  if (onOnlineCallback) onOnlineCallback();
};

/**
 * Record a failure and potentially open circuit
 */
const recordFailure = () => {
  circuitBreakerFailures++;

  if (circuitBreakerFailures >= CIRCUIT_BREAKER_THRESHOLD) {
    circuitBreakerOpenUntil = Date.now() + CIRCUIT_BREAKER_RESET_MS;
    console.warn(`🔴 Circuit breaker OPEN - blocking requests for ${CIRCUIT_BREAKER_RESET_MS / 1000}s after ${circuitBreakerFailures} failures`);
    if (onOfflineCallback) onOfflineCallback();
  }
};

/**
 * Calculate exponential backoff delay with jitter
 */
const getRetryDelay = (retryCount) => {
  const delay = INITIAL_RETRY_DELAY_MS * Math.pow(2, retryCount);
  // Add jitter (0-500ms) to prevent thundering herd
  const jitter = Math.random() * 500;
  return Math.min(delay + jitter, MAX_RETRY_DELAY_MS);
};

/**
 * Check if error is a network/transient error (retryable)
 */
const isNetworkError = (error) => {
  // No response received (network down, timeout, DNS failure)
  if (!error.response) {
    return true;
  }
  return false;
};

/**
 * Check if error is retryable (transient issues)
 */
const isRetryableError = (error) => {
  // Network errors are retryable
  if (isNetworkError(error)) {
    return true;
  }

  // Server errors that might be transient
  const status = error.response?.status;
  return status === 408 || // Request Timeout
         status === 429 || // Too Many Requests
         status === 500 || // Internal Server Error
         status === 502 || // Bad Gateway
         status === 503 || // Service Unavailable
         status === 504;   // Gateway Timeout
};

/**
 * Tag error with offline/transient flags for easier handling
 */
const tagError = (error) => {
  const isNetwork = isNetworkError(error);
  const isTransient = isRetryableError(error);

  // Add flags to error object
  error.isOffline = isNetwork;
  error.isTransient = isTransient;
  error.isNetworkError = isNetwork;

  // Normalize error message for display
  if (isNetwork) {
    error.userMessage = 'Connection interrupted. Please wait...';
  } else if (error.response?.status >= 500) {
    error.userMessage = 'Server temporarily unavailable';
  } else {
    error.userMessage = error.message || 'Request failed';
  }

  return error;
};

/**
 * Sleep helper for retry delays
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Create axios instance with retry logic and circuit breaker
 */
export const createApiClient = () => {
  const instance = axios.create({
    timeout: DEFAULT_TIMEOUT_MS
  });

  // Request interceptor - set baseURL dynamically + check circuit breaker
  instance.interceptors.request.use((config) => {
    // Check circuit breaker
    if (isCircuitOpen()) {
      const error = new Error('Circuit breaker is open - request blocked');
      error.code = 'CIRCUIT_OPEN';
      error.isOffline = true;
      error.isTransient = true;
      error.userMessage = 'Connection interrupted. Reconnecting...';
      return Promise.reject(error);
    }

    // Set baseURL from current dynamic config
    config.baseURL = getApiBaseUrl_dynamic();
    return config;
  });

  // Response interceptor - handle retries and track failures
  instance.interceptors.response.use(
    (response) => {
      // Success - reset failure counter (but not full circuit reset)
      if (circuitBreakerFailures > 0) {
        circuitBreakerFailures = Math.max(0, circuitBreakerFailures - 1);
      }
      return response;
    },
    async (error) => {
      const config = error.config || {};

      // Initialize retry count
      config._retryCount = config._retryCount || 0;

      // Tag error with flags
      tagError(error);

      // Check if we should retry
      if (config._retryCount < MAX_RETRIES && isRetryableError(error) && !isCircuitOpen()) {
        config._retryCount++;

        const delay = getRetryDelay(config._retryCount - 1);
        console.log(`🔄 Retry ${config._retryCount}/${MAX_RETRIES} for ${config.url} in ${Math.round(delay)}ms`);

        await sleep(delay);

        // Update baseURL in case it changed
        config.baseURL = getApiBaseUrl_dynamic();

        return instance.request(config);
      }

      // Final failure - record for circuit breaker
      if (error.isNetworkError) {
        recordFailure();
      }

      // Log final failure (but keep it concise for kiosk)
      const endpoint = config.url || 'unknown';
      console.error(`❌ Request failed: ${endpoint}`, {
        message: error.message,
        status: error.response?.status,
        code: error.code,
        retries: config._retryCount,
        circuitFailures: circuitBreakerFailures
      });

      return Promise.reject(error);
    }
  );

  return instance;
};

// Default client instance
const apiClient = createApiClient();

/**
 * Safe GET request - NEVER throws, returns { data, error }
 * Use for polling/background requests where failures should be silent
 */
export const safeGet = async (url, config = {}) => {
  try {
    const response = await apiClient.get(url, config);
    return { data: response.data, error: null };
  } catch (error) {
    tagError(error);
    return { data: null, error };
  }
};

/**
 * Safe POST request - NEVER throws, returns { data, error }
 */
export const safePost = async (url, data, config = {}) => {
  try {
    const response = await apiClient.post(url, data, config);
    return { data: response.data, error: null };
  } catch (error) {
    tagError(error);
    return { data: null, error };
  }
};

/**
 * Safe DELETE request - NEVER throws, returns { data, error }
 */
export const safeDelete = async (url, config = {}) => {
  try {
    const response = await apiClient.delete(url, config);
    return { data: response.data, error: null };
  } catch (error) {
    tagError(error);
    return { data: null, error };
  }
};

/**
 * Safe PUT request - NEVER throws, returns { data, error }
 */
export const safePut = async (url, data, config = {}) => {
  try {
    const response = await apiClient.put(url, data, config);
    return { data: response.data, error: null };
  } catch (error) {
    tagError(error);
    return { data: null, error };
  }
};

export default apiClient;
